import React from 'react';
import LibrarySection from './LibrarySection';
import ContactForm from './ContactForm';
import { luminance, themedSection } from 'pages/useSiteTheme';
import { blockStyle, buildBlockTree } from './blockStyles';
import { renderRichText } from './richText';
import { DetailBreadcrumb, DetailMore, DetailPrevNext } from './DetailExtras';

const DEFAULT_ACCENT = '#422AFB';

/**
 * SiteRenderer — turns the (Page → Section → Component) tree returned by the
 * backend into rendered HTML. Used by both the live preview in the editor and
 * the public brand site.
 *
 * The fallback accent, used only when a brand has not chosen a colour. Named
 * rather than repeated, because the point of it is that it should appear as
 * rarely as possible.
 *
 * Intentionally framework-free styling (inline styles + CSS) so the public
 * brand site stays lean and renders identically inside the editor preview.
 */

const containerStyleForLayout = (layout) => {
  switch (layout) {
    case 'fullwidth':
      return { width: '100%', maxWidth: 'none' };
    case 'sidebar':
      return { width: '100%', maxWidth: '1100px', margin: '0 auto' };
    case 'custom':
      return { width: '100%' };
    case 'default':
    default:
      return { width: '100%', maxWidth: '1200px', margin: '0 auto' };
  }
};

/**
 * How each section TYPE lays itself out.
 *
 * Until now `section.type` was stored, sent to the client and written into a
 * data attribute, but never once affected rendering — a hero, a features
 * grid and a pricing table all came out as the same centred column of
 * stacked components. That is the real reason templates looked thin: there
 * were five of them and they all produced the same page.
 *
 * `intro` marks the types where the leading heading and paragraph are the
 * section's own introduction and belong full width, with everything after
 * them laid out in a grid. That is how these sections are actually written —
 * "Why customers love us", a sentence, then the three reasons.
 */
const SECTION_LAYOUT = {
  hero:         { align: 'center', maxWidth: 860, padY: 96, scale: 'clamp(34px, 5.5vw, 60px)' },
  cta:          { align: 'center', maxWidth: 720, padY: 72, scale: 'clamp(26px, 3.5vw, 38px)' },
  contact:      { align: 'center', maxWidth: 680, padY: 72 },
  content:      { maxWidth: 760, padY: 64 },
  faq:          { maxWidth: 800, padY: 64 },
  features:     { grid: 260, intro: true, padY: 72 },
  cards:        { grid: 280, intro: true, padY: 72, card: true },
  pricing:      { grid: 280, intro: true, padY: 72, card: true },
  testimonials: { grid: 300, intro: true, padY: 72, card: true },
  team:         { grid: 220, intro: true, padY: 72, align: 'center' },
  gallery:      { grid: 200, intro: true, padY: 64 },
  header:       { row: true, padY: 16, maxWidth: 1200 },
  footer:       { row: true, padY: 40, maxWidth: 1200 },
};

const layoutFor = (type) => SECTION_LAYOUT[type] || { maxWidth: 1000, padY: 56 };

/**
 * What a content type is called in a breadcrumb.
 *
 * Only the types a detail page can be built on, because that is the only
 * place this is read — a trail saying "Home / blogs / …" would be the raw
 * key, which is not a word anyone writes on a website.
 */
const LIBRARY_LABELS = {
  blogs: 'Blog', news: 'News', projects: 'Projects', services: 'Services',
  ourteam: 'Team', ourpartners: 'Partners', press: 'Press', events: 'Events',
  jobs: 'Jobs', policies: 'Policies', gallery: 'Gallery', videos: 'Videos',
};

const sectionWrapperStyle = (section, isDark) => {
  const { bg, fg } = themedSection(section, isDark);
  return sectionWrapperStyleFor({ ...section, backgroundColor: bg, textColor: fg });
};

const sectionWrapperStyleFor = (section) => ({
  width: '100%',
  backgroundColor: section.backgroundColor && section.backgroundColor !== 'transparent'
    ? section.backgroundColor
    : 'transparent',
  color: section.textColor || 'inherit',
  paddingTop: section.paddingTop ? `${section.paddingTop}px` : `${layoutFor(section.type).padY}px`,
  paddingBottom: section.paddingBottom ? `${section.paddingBottom}px` : `${layoutFor(section.type).padY}px`,
  paddingLeft: section.paddingLeft ? `${section.paddingLeft}px` : '16px',
  paddingRight: section.paddingRight ? `${section.paddingRight}px` : '16px',
  marginTop: section.marginTop ? `${section.marginTop}px` : '0',
  marginBottom: section.marginBottom ? `${section.marginBottom}px` : '0',
  ...(section.backgroundImage
    ? { backgroundImage: `url(${section.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : {}),
});

const sectionInnerStyle = (section) => {
  const L = layoutFor(section.type);
  const base = {
    margin: '0 auto',
    maxWidth: `${L.maxWidth || 1200}px`,
    ...(L.align === 'center' ? { textAlign: 'center' } : {}),
  };
  // An explicit layout choice still wins over the type's default.
  if (section.layout === 'full') return { ...base, maxWidth: '100%' };
  if (section.layout === 'boxed') return { ...base, maxWidth: '960px' };
  return base;
};

// ────────────── Component renderers ─────────────────────────────────────────

const Heading = ({ component, scale }) => {
  const c = component.content || {};
  const Tag = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(c.level) ? c.level : 'h2';
  const style = {
    color: component.styles?.textColor || 'inherit',
    margin: '0 0 16px',
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    // Browser defaults size an h1 the same wherever it sits, so a hero
    // headline came out no larger than a paragraph heading. The section's
    // scale overrides that, and clamps so it still fits a phone.
    ...(scale ? { fontSize: scale } : {}),
    textWrap: 'balance',
    // The owner's own choices come last so they beat both the browser default
    // and the section's display scale — a heading set to 20px is set to 20px
    // even in a hero.
    ...blockStyle(component),
  };
  return <Tag style={style}>{c.text || 'Heading'}</Tag>;
};

const TextBlock = ({ component }) => {
  const c = component.content || {};
  return (
    <p style={{
      color: component.styles?.textColor || 'inherit',
      margin: '0 0 16px',
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
      ...blockStyle(component),
    }}>
      {c.text || ''}
    </p>
  );
};

const ImageBlock = ({ component }) => {
  const c = component.content || {};
  if (!c.src) {
    return (
      <div style={{
        width: '100%',
        minHeight: 160,
        background: '#f3f4f6',
        border: '1px dashed #d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6b7280',
        fontSize: 12,
        borderRadius: 8,
        marginBottom: 16,
      }}>
        Image placeholder
      </div>
    );
  }
  return (
    <figure style={{ margin: '0 0 16px', ...blockStyle(component) }}>
      <img
        src={c.src}
        alt={c.alt || ''}
        style={{
          width: '100%', height: 'auto', display: 'block', borderRadius: 8,
          ...(component.styles?.maxWidth ? { maxWidth: `${component.styles.maxWidth}px` } : {}),
          ...(component.styles?.textAlign === 'center' ? { margin: '0 auto' } : {}),
          ...(component.styles?.textAlign === 'right' ? { marginLeft: 'auto' } : {}),
        }}
      />
      {c.caption && (
        <figcaption style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
          {c.caption}
        </figcaption>
      )}
    </figure>
  );
};

/**
 * The brand's own colour, for the things that should carry it.
 *
 * Buttons were drawn in #422AFB — TechProOne's indigo — on every brand's
 * site. On a brand whose colour is anything else that is simply wrong, and on
 * a brand serving this from its own domain it is our colour appearing on
 * their page, which is the one thing a white-labelled site must not do.
 *
 * A context rather than a prop: a button sits several layers down inside
 * sections, rows and columns, none of which have any business knowing about
 * a palette in order to pass it on.
 */
const AccentContext = React.createContext(DEFAULT_ACCENT);

/** White or near-black, whichever can actually be read on `hex`. */
const inkOn = (hex) => (luminance(hex) > 0.55 ? '#0F172A' : '#ffffff');

const ButtonBlock = ({ component }) => {
  const accent = React.useContext(AccentContext);
  const c = component.content || {};
  const variant = c.variant || 'primary';
  const base = {
    display: 'inline-block',
    padding: '10px 22px',
    borderRadius: 9999,
    fontWeight: 600,
    textDecoration: 'none',
    fontSize: 14,
    transition: 'transform 0.15s',
  };
  const variants = {
    // Not always white on the accent: a brand whose colour is a light lime or
    // a yellow gets near-black, or the label is unreadable on its own button.
    primary:   { background: accent, color: inkOn(accent), border: `1px solid ${accent}` },
    secondary: { background: '#0F172A', color: '#fff', border: '1px solid #0F172A' },
    outline:   { background: 'transparent', color: accent, border: `1px solid ${accent}` },
    ghost:     { background: 'transparent', color: '#0F172A', border: '1px solid transparent' },
  };
  const style = { ...base, ...(variants[variant] || variants.primary), ...blockStyle(component) };
  return (
    <a href={c.url || '#'} target={c.target || '_self'} style={style}>
      {c.text || 'Click'}
    </a>
  );
};

const VideoBlock = ({ component }) => {
  const c = component.content || {};
  if (!c.src) return null;
  const isYouTube = /youtube\.com|youtu\.be/.test(c.src);
  if (isYouTube) {
    const videoId = (c.src.match(/(?:youtu\.be\/|v=)([\w-]{11})/) || [])[1];
    if (videoId) {
      return (
        <div style={{ position: 'relative', paddingTop: '56.25%', marginBottom: 16, borderRadius: 8, overflow: 'hidden' }}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="Video"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
  }
  return (
    <video controls style={{ width: '100%', borderRadius: 8, marginBottom: 16 }} src={c.src} />
  );
};

const Divider = () => (
  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '24px 0' }} />
);

const Spacer = ({ component }) => {
  const h = component.content?.height || 24;
  return <div style={{ height: h }} />;
};

const Unknown = ({ component }) => (
  <div style={{
    background: '#fff7ed',
    border: '1px dashed #fdba74',
    color: '#9a3412',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 12,
    marginBottom: 12,
  }}>
    Component type "{component.type}" not implemented in renderer
  </div>
);

/**
 * A grid of whatever is put inside it.
 *
 * Column counts come out as a real stylesheet rather than an inline style,
 * because the useful part of a grid is what it does when the screen is
 * narrow, and `style={}` cannot express a media query. Three columns that
 * stay three columns at 360px is not a grid, it is a horizontal scrollbar.
 *
 * The class name is derived from the block's own id, so two grids on a page
 * never collide and a grid's rules travel with it.
 */
const GridBlock = ({ component, isDark }) => {
  const c = component.content || {};
  const cls = `tpo-g-${String(component._id || '').replace(/[^a-zA-Z0-9]/g, '').slice(-10) || 'x'}`;
  const cols = Math.min(6, Math.max(1, Number(c.columns) || 3));
  const gap = Math.min(80, Math.max(0, Number(c.gap ?? 24)));
  const mid = Math.min(cols, 2);

  return (
    <>
      <style>{
        `.${cls}{display:grid;gap:${gap}px;grid-template-columns:repeat(${cols},minmax(0,1fr));align-items:${c.align || 'stretch'}}`
        + `@media(max-width:900px){.${cls}{grid-template-columns:repeat(${mid},minmax(0,1fr))}}`
        + `@media(max-width:600px){.${cls}{grid-template-columns:1fr}}`
      }</style>
      <div className={cls} style={{ marginBottom: 16, ...blockStyle(component) }}>
        {(component.children || []).map((child) => renderComponent(child, undefined, isDark))}
      </div>
    </>
  );
};

/**
 * A boxed group — one plan in a pricing table, one person on a team page.
 *
 * Tinted rather than given a hard border and a white fill: it has to sit on
 * whatever background the owner picked, and a white card on a dark section is
 * a hole in the page.
 */
const CardBlock = ({ component, isDark }) => {
  const c = component.content || {};
  const style = {
    background: c.plain ? 'transparent' : 'rgba(127,127,127,0.07)',
    border: c.plain ? 'none' : '1px solid rgba(127,127,127,0.16)',
    borderRadius: 14,
    padding: 22,
    ...blockStyle(component),
  };
  return (
    <div style={style}>
      {(component.children || []).map((child) => renderComponent(child, undefined, isDark))}
    </div>
  );
};

const RENDERERS = {
  heading: Heading,
  text: TextBlock,
  image: ImageBlock,
  button: ButtonBlock,
  video: VideoBlock,
  divider: Divider,
  spacer: Spacer,
  grid: GridBlock,
  card: CardBlock,
};

const renderComponent = (component, scale, isDark) => {
  const Renderer = RENDERERS[component.type] || Unknown;
  return <Renderer key={component._id} component={component} scale={scale} isDark={isDark} />;
};

/**
 * One library entry rendered as the body of a detail page.
 *
 * Deliberately simple and unstyled beyond typography: this is somebody's blog
 * post or case study, and the brand's own section colours already surround
 * it. Whitespace in the stored text becomes paragraphs, which is what people
 * expect from a textarea and what every one of these editors provides.
 */
const DetailEntry = ({ entry, textColor, options, siblings, listPath, typeLabel, listSlug, currentSlug }) => {
  const title = entry.title || entry.name || '';
  const meta = entry.author || entry.role || entry.date || '';
  const body = entry.content || entry.description || entry.bio || entry.text || '';
  const image = entry.image || entry.photo || entry.logo || '';

  const more = (
    <DetailMore
      options={options?.more}
      siblings={siblings}
      currentSlug={currentSlug}
      listSlug={listSlug}
      textColor={textColor}
    />
  );

  return (
    <article style={{ color: textColor, maxWidth: 760, margin: '0 auto' }}>
      <DetailBreadcrumb
        options={options?.breadcrumb}
        listPath={listPath}
        typeLabel={typeLabel}
        title={title}
        textColor={textColor}
      />
      {options?.more?.position === 'above' && more}
      {title && <h1 style={{ fontSize: 34, lineHeight: 1.2, margin: '0 0 8px' }}>{title}</h1>}
      {meta && <div style={{ opacity: 0.65, fontSize: 14, marginBottom: 20 }}>{meta}</div>}
      {image && (
        <img
          src={image}
          alt={title}
          style={{ width: '100%', borderRadius: 12, marginBottom: 24, objectFit: 'cover' }}
        />
      )}
      {/* Headings, emphasis, lists and links, from what the author wrote.
          Plain text with blank lines between paragraphs still comes out as
          paragraphs, which is what every entry written before this was. */}
      {renderRichText(body, { scale: 17 })}

      <DetailPrevNext
        options={options?.prevNext}
        siblings={siblings}
        currentSlug={currentSlug}
        listSlug={listSlug}
        textColor={textColor}
      />
      {options?.more?.position !== 'above' && more}
    </article>
  );
};

// `data-section-id` is what the visual editor hit-tests against. Harmless on a
// live page, and it lets the editor operate on the real rendered component
// rather than an approximation — what you click is what ships.
/**
 * Splits a section's components into its introduction and its body.
 *
 * In a grid section the opening heading — and a paragraph immediately after
 * it — introduce the whole section and belong across the full width; the
 * items that follow are what gets gridded. Reading the split from the content
 * rather than asking the owner to declare it means existing pages gain the
 * layout without anyone editing them.
 */
const splitIntro = (components) => {
  const out = { intro: [], body: [...components] };
  if (out.body[0]?.type === 'heading') out.intro.push(out.body.shift());
  if (out.intro.length && out.body[0]?.type === 'text') out.intro.push(out.body.shift());
  return out;
};

const SectionBody = ({ section, brandRef, pageSlug, isDark }) => {
  const L = layoutFor(section.type);
  // The API sends every block in the section flat, each carrying the id of
  // the block it belongs inside. The shape is recovered here, once, and only
  // the top-level blocks are laid out — a card's contents are the card's
  // business, not the section's.
  const components = buildBlockTree(section.componentData || []);

  // A contact section gets an actual form under its copy. It used to render
  // a heading and a button, which looked like a contact page and collected
  // nothing.
  const contactForm = section.type === 'contact' ? (
    <div style={{ marginTop: 24 }}>
      <ContactForm
        section={section}
        textColor={section.textColor || '#0f172a'}
        brandRef={brandRef}
        pageSlug={pageSlug}
      />
    </div>
  ) : null;

  // A section type that grids whatever it is given was the only way to get a
  // row of anything, and it guessed: add a note under three pricing cards and
  // it became a fourth card. Once the owner places a grid of their own, that
  // guess stops — they have said what they want arranged and what they
  // haven't.
  const arrangedByOwner = components.some((c) => c.type === 'grid');

  if (!L.grid || arrangedByOwner) {
    // Only the section's own headline takes the display size; a second
    // heading further down is a sub-heading and should not shout too.
    let used = false;
    return (
      <>
        {components.map((c) => {
          const scale = (!used && L.scale && c.type === 'heading') ? L.scale : undefined;
          if (scale) used = true;
          return renderComponent(c, scale, isDark);
        })}
        {contactForm}
      </>
    );
  }

  const { intro, body } = L.intro ? splitIntro(components) : { intro: [], body: components };

  return (
    <>
      {intro.length > 0 && (
        <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 36px' }}>
          {/* Not `.map(renderComponent)` — map passes (item, index), so the
              index would arrive as `scale` and a heading could end up sized
              in single-digit pixels. */}
          {intro.map((c) => renderComponent(c, undefined, isDark))}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          // min() so a 280px track cannot overflow a 320px phone.
          gridTemplateColumns: `repeat(auto-fit, minmax(min(${L.grid}px, 100%), 1fr))`,
          gap: 24,
          textAlign: L.align === 'center' ? 'center' : 'left',
        }}
      >
        {body.map((c) => (
          <div
            key={c._id}
            style={L.card ? {
              // A tinted card rather than a hard border: it has to sit on
              // whatever background the owner picked, light or dark.
              background: 'rgba(127,127,127,0.07)',
              border: '1px solid rgba(127,127,127,0.16)',
              borderRadius: 14,
              padding: 22,
            } : undefined}
          >
            {renderComponent(c, undefined, isDark)}
          </div>
        ))}
      </div>
    </>
  );
};

const SectionRenderer = ({
  section, detailEntry, isDark, brandRef, pageSlug, editorMode,
  detailSiblings = [], detailListPath = '', typeLabel = '', entrySlug = '',
}) => (
  <section
    style={{
      ...sectionWrapperStyle(section, isDark),
      // In the editor a hidden section still has to be visible — it is the
      // thing being built — but it must not look like it is on the live site.
      // The public renderer never receives hidden sections at all, so this
      // only ever applies to the canvas.
      ...(editorMode && section.isVisible === false
        ? { opacity: 0.45, position: 'relative' }
        : {}),
    }}
    data-section-id={section._id}
    data-region={section.region}
    data-type={section.type}
  >
    {editorMode && section.isVisible === false && (
      <div style={{
        position: 'absolute', top: 8, right: 8, zIndex: 2,
        background: '#0f172a', color: '#fff',
        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
        padding: '3px 8px', borderRadius: 999, textTransform: 'uppercase',
      }}>
        Hidden
      </div>
    )}
    <div style={sectionInnerStyle(section)}>
      {/* A section either owns its components or shows library content; the
          two never mix, so the binding decides which renderer runs. */}
      {section.source?.kind === 'library'
        ? <LibrarySection section={section} textColor={section.textColor || '#0f172a'} />
        : <SectionBody section={section} brandRef={brandRef} pageSlug={pageSlug} isDark={isDark} />}
      {/* On a detail page, the section flagged to carry the entry renders it.
          Marking one section rather than injecting at the top lets the owner
          keep a header, a hero and a footer around the post. */}
      {section.showDetailEntry && detailEntry && (
        <DetailEntry
          entry={detailEntry}
          textColor={section.textColor || '#0f172a'}
          options={section.detailOptions}
          siblings={detailSiblings}
          listPath={detailListPath}
          typeLabel={typeLabel}
          listSlug={pageSlug}
          currentSlug={entrySlug}
        />
      )}
    </div>
  </section>
);

export default function SiteRenderer({
  page, brand, isDark = false, brandRef, editorMode = false, entrySlug = '',
}) {
  if (!page) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        Nothing published yet.
      </div>
    );
  }

  const sections = page.sectionData || [];
  const regions = { header: [], main: [], footer: [] };
  sections.forEach((s) => {
    const r = ['header', 'main', 'footer'].includes(s.region) ? s.region : 'main';
    regions[r].push(s);
  });
  Object.values(regions).forEach((arr) =>
    arr.sort((a, b) => (a.order || 0) - (b.order || 0))
  );

  const rootStyle = {
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    background: isDark ? '#0f1115' : '#fff',
    color: isDark ? '#e5e7eb' : '#0f172a',
    ...containerStyleForLayout(page.layout),
  };

  const accent = brand?.brand_color || brand?.company_color || DEFAULT_ACCENT;

  return (
    <AccentContext.Provider value={accent}>
    <div style={rootStyle}>
      {page.customCss && <style>{page.customCss}</style>}
      <header>
        {regions.header.map((s) => <SectionRenderer key={s._id} section={s} detailEntry={page.detailEntry} isDark={isDark} brandRef={brandRef} pageSlug={page.slug} editorMode={editorMode}
          detailSiblings={page.detailSiblings || []} detailListPath={page.detailListPath || ''}
          typeLabel={LIBRARY_LABELS[page.detailOf] || ''} entrySlug={entrySlug} />)}
      </header>
      <main>
        {regions.main.map((s) => <SectionRenderer key={s._id} section={s} detailEntry={page.detailEntry} isDark={isDark} brandRef={brandRef} pageSlug={page.slug} editorMode={editorMode}
          detailSiblings={page.detailSiblings || []} detailListPath={page.detailListPath || ''}
          typeLabel={LIBRARY_LABELS[page.detailOf] || ''} entrySlug={entrySlug} />)}
        {regions.main.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
            <h1 style={{ margin: '0 0 8px' }}>{page.title || brand?.name || 'Untitled'}</h1>
            <p style={{ margin: 0, fontSize: 14 }}>This page doesn't have any content yet.</p>
          </div>
        )}
      </main>
      <footer>
        {regions.footer.map((s) => <SectionRenderer key={s._id} section={s} detailEntry={page.detailEntry} isDark={isDark} brandRef={brandRef} pageSlug={page.slug} editorMode={editorMode}
          detailSiblings={page.detailSiblings || []} detailListPath={page.detailListPath || ''}
          typeLabel={LIBRARY_LABELS[page.detailOf] || ''} entrySlug={entrySlug} />)}
      </footer>
      {page.customJs && (
        // eslint-disable-next-line react/no-danger
        <script dangerouslySetInnerHTML={{ __html: page.customJs }} />
      )}
    </div>
    </AccentContext.Provider>
  );
}
